import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Box, Flex, Icon } from '@chakra-ui/react';
import { components } from 'react-select';
import { ChevronRight } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';
import { CheckCircle } from '@kula-ai/icons/dual-tone/CheckCircle';

const getItemPath = (item, items) => {
  const path = [item];
  let current = item;

  while (current?.parent_id) {
    const parent = items.find((i) => i.id === current.parent_id);
    if (parent) {
      path.unshift(parent);
      current = parent;
    } else {
      break;
    }
  }

  return path;
};

// Helper function to get all children of an item
const getAllChildren = (item, items) => {
  const children = items.filter((i) => i.parent_id === item.id);
  let allChildren = [...children];

  children.forEach((child) => {
    allChildren = [...allChildren, ...getAllChildren(child, items)];
  });

  return allChildren;
};

// Search result item component
const SearchResultItem = ({
  item,
  items,
  onSelect,
  isSelected,
  isHighlighted,
  itemRef,
  onKeyDown: _onKeyDown,
}: {
  item: any;
  items: any;
  onSelect: any;
  isSelected: any;
  isHighlighted: any;
  itemRef: any;
  onKeyDown?: (e: any) => void;
}) => {
  const path = getItemPath(item, items);
  const displayPath =
    path.length <= 3
      ? path
      : [path[0], { id: 'ellipsis', name: '...' }, ...path.slice(-2)];

  return (
    <Box
      ref={itemRef}
      p=".4em .5em"
      cursor="pointer"
      _hover={{ bgColor: 'secondary-25' }}
      borderRadius="6px"
      onClick={() => onSelect(item, true)}
      bgColor={isHighlighted ? 'secondary-25' : 'transparent'}
      tabIndex={0}
    >
      <Flex alignItems="center" gap="2">
        {displayPath.map((pathItem, index) => (
          <React.Fragment key={pathItem.id}>
            <KText
              size="sm-medium"
              color={isSelected ? 'primary-600' : 'secondary-700'}
              fontWeight={isHighlighted ? 'bold' : 'normal'}
            >
              {pathItem.name}
            </KText>
            {index < displayPath.length - 1 && (
              <ChevronRight color="secondary-700" size={20} />
            )}
          </React.Fragment>
        ))}
        {isSelected && <Icon as={CheckCircle} color="primary-500" ml="auto" />}
      </Flex>
    </Box>
  );
};

export const TreeItem = ({
  item,
  items,
  primaryParentStyles = {},
  childrenStyles = {},
  onSelect = (_data: any, _flag?: boolean) => {},
  onClose,
  inputValue,
  currentSelectedValue,
  isMulti,
  isHighlighted = false,
  itemRef,
  focusedItemId,
  onKeyDown: _onKeyDown,
}: {
  item: any;
  items: any;
  primaryParentStyles?: Record<string, any>;
  childrenStyles?: Record<string, any>;
  onSelect?: (data: any, flag?: boolean) => void;
  onClose: any;
  inputValue: any;
  currentSelectedValue: any;
  isMulti: any;
  isHighlighted?: boolean;
  itemRef: any;
  focusedItemId: any;
  onKeyDown?: (e: any) => void;
}) => {
  const isDisabled = item.isDisabled;
  const children = items?.filter((child) => child?.parent_id === item?.id);
  const isPrimaryParent = item.parent_id === null;
  const stylesToApply = isPrimaryParent ? primaryParentStyles : childrenStyles;
  const isFocused = focusedItemId === item.id;
  const isSelected = isMulti
    ? currentSelectedValue?.some((value) => value?.id === item?.id)
    : currentSelectedValue?.id === item?.id;

  const handleSelect = (event) => {
    event.stopPropagation();
    if (isDisabled) return;
    if (onSelect) {
      onSelect(item, false); // Pass false to indicate this is from tree view
      if (!isMulti) {
        onClose?.();
      }
    }
  };

  const refCallback = (element) => {
    if ((isHighlighted || isFocused) && element && itemRef) {
      itemRef(element);
    }
  };

  return (
    <Box {...stylesToApply} ref={refCallback} data-item-id={item.id}>
      <Flex
        alignItems="center"
        p=".4em .5em"
        gap=".5em"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        _hover={{ bgColor: !isDisabled && 'secondary-25' }}
        borderRadius="6px"
        opacity={isDisabled ? 0.3 : 1}
        onClick={handleSelect}
        bgColor={isHighlighted || isFocused ? 'secondary-25' : 'transparent'}
        tabIndex={0}
      >
        <KText
          size={isPrimaryParent ? 'sm-semi-bold' : 'sm-medium'}
          color={isSelected ? 'primary-600' : 'secondary-700'}
          fontWeight={isHighlighted || isFocused ? 'bold' : 'normal'}
          isTruncated
          enableTooltip={true}
          maxW="100%"
        >
          {item?.name}
        </KText>
        {isSelected && <Icon as={CheckCircle} color="primary-500" />}
      </Flex>
      {children?.length > 0 &&
        children?.map((child) => {
          const childMatches =
            inputValue?.length > 0 &&
            child?.name
              ?.toLowerCase()
              ?.includes(inputValue?.trim()?.toLowerCase());

          return (
            <TreeItem
              key={child.id}
              item={child}
              items={items}
              onClose={onClose}
              inputValue={inputValue}
              currentSelectedValue={currentSelectedValue}
              primaryParentStyles={primaryParentStyles}
              childrenStyles={{
                marginLeft: '24px',
                padding: '.25em .5em',
                borderLeft: '1px solid',
                borderColor: 'secondary-200',
                cursor: 'pointer',
              }}
              onSelect={onSelect}
              isMulti={isMulti}
              isHighlighted={childMatches}
              itemRef={itemRef}
              focusedItemId={focusedItemId}
            />
          );
        })}
    </Box>
  );
};

const NestedTreeList = ({
  data,
  // No right padding by default — the inner Flex already supplies horizontal
  // padding, and `pr: '1.5em'` made parent rows stretch unnecessarily wide.
  primaryParentStyles = {},
  childrenStyles = {
    marginLeft: '10px',
    padding: '.25em .5em',
    borderLeft: '1px solid',
    borderColor: 'border-color',
    cursor: 'pointer',
  },
  onSelect = (_item?: any, _isFromSearch?: boolean) => {},
  onClose,
  inputValue,
  currentSelectedValue,
  isMulti,
  menuIsOpen,
}) => {
  const scrollContainerRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const previousInputValue = useRef(inputValue);
  const initialScrollDoneRef = useRef(false);
  const [focusedItemId, setFocusedItemId] = useState(null);

  // Named function expression — `flatten` inside the body refers to itself,
  // which sidesteps the "used before declared" error from referencing the
  // outer `const getAllItems` while still being defined.
  const getAllItems = useCallback(
    function flatten(items) {
      let flatItems = [];
      items.forEach((item) => {
        flatItems.push(item);
        const children = data.filter((child) => child.parent_id === item.id);
        if (children.length > 0) {
          flatItems = [...flatItems, ...flatten(children)];
        }
      });
      return flatItems;
    },
    [data]
  );

  const getFilteredItems = useCallback(() => {
    if (!inputValue?.trim()) return [];

    const searchTerm = inputValue?.toLowerCase()?.trim();
    const ungroupedItems = [];
    const groupedItems = [];

    // First find all matching items
    data.forEach((item) => {
      if (item?.name?.toLowerCase()?.includes(searchTerm)) {
        // Separate ungrouped (no parent) and grouped items
        if (item.parent_id === null) {
          ungroupedItems?.push(item);
        } else {
          groupedItems?.push(item);
        }
      }
    });

    // Combine with ungrouped first, then grouped
    return [...ungroupedItems, ...groupedItems];
  }, [inputValue, data]);

  const scrollToRef = useCallback(
    (element) => {
      const trimmedInput = inputValue?.trim() || '';

      if (
        (trimmedInput.length > 0 || focusedItemId) &&
        element &&
        !hasScrolledRef.current
      ) {
        hasScrolledRef.current = true;

        setTimeout(() => {
          const container = scrollContainerRef.current;
          if (container) {
            const elementOffsetTop = element.offsetTop;
            const containerHeight = container.clientHeight;
            const elementHeight = element.clientHeight;

            const newScrollTop =
              elementOffsetTop - (containerHeight - elementHeight) / 2;

            container.scrollTo({
              top: newScrollTop,
              behavior: 'auto',
            });
          } else {
            hasScrolledRef.current = false;
          }
        }, 100);
      }
    },
    [inputValue, focusedItemId]
  );

  const handleKeyDown = useCallback(
    (e, item) => {
      e.preventDefault();
      const rootItems = data?.filter((item) => item?.parent_id === null);
      const allItems = getAllItems(rootItems);
      const currentIndex = allItems.findIndex((i) => i.id === item.id);

      switch (e.key) {
        case 'ArrowDown':
          if (currentIndex < allItems.length - 1) {
            setFocusedItemId(allItems[currentIndex + 1].id);
          }
          break;
        case 'ArrowUp':
          if (currentIndex > 0) {
            setFocusedItemId(allItems[currentIndex - 1].id);
          }
          break;
        case 'Enter':
          onSelect(item, inputValue?.trim()?.length > 0);
          if (!isMulti) {
            onClose?.();
          }
          break;
        default:
          break;
      }
    },
    [data, getAllItems, onSelect, isMulti, onClose, inputValue]
  );

  useEffect(() => {
    if (menuIsOpen) {
      hasScrolledRef.current = false;
      const selectedId = isMulti
        ? currentSelectedValue?.[0]?.id
        : currentSelectedValue?.id;

      if (selectedId) {
        setFocusedItemId(selectedId);

        const selectedItem = data.find((item) => item.id === selectedId);
        if (selectedItem) {
          const path = getItemPath(selectedItem, data);
          path.forEach((item) => {
            if (item.parent_id) {
              const parentElement = document.querySelector(
                `[data-item-id="${item.parent_id}"]`
              );
              if (parentElement) {
                parentElement.setAttribute('data-expanded', 'true');
              }
            }
          });
        }

        setTimeout(() => {
          const element = document.querySelector(
            `[data-item-id="${selectedId}"]`
          );
          if (element && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const scrollTop =
              (element as HTMLElement).offsetTop -
              (container.clientHeight - (element as HTMLElement).clientHeight) /
                2;

            container.scrollTo({
              top: scrollTop,
              behavior: 'auto',
            });
          }
        }, 50);
      }
    }
  }, [menuIsOpen, currentSelectedValue, isMulti, data]);

  useEffect(() => {
    if (
      !initialScrollDoneRef.current &&
      currentSelectedValue &&
      scrollContainerRef.current
    ) {
      initialScrollDoneRef.current = true;
      const selectedId = isMulti
        ? currentSelectedValue[0]?.id
        : currentSelectedValue?.id;

      if (selectedId) {
        setFocusedItemId(selectedId);
        hasScrolledRef.current = false;
      }
    }

    return () => {
      initialScrollDoneRef.current = false;
    };
  }, [currentSelectedValue, isMulti]);

  if (previousInputValue.current !== inputValue) {
    hasScrolledRef.current = false;
  }

  if (
    previousInputValue.current?.length > 0 &&
    (!inputValue || inputValue.length === 0)
  ) {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    }
  }

  previousInputValue.current = inputValue;

  const rootItems = data?.filter((item) => item?.parent_id === null);
  const filteredItems = inputValue?.trim() ? getFilteredItems() : [];

  if (inputValue?.trim() && filteredItems.length === 0) {
    return (
      <Flex minH="2em" justifyContent="center" alignItems="center">
        <KText>No options found</KText>
      </Flex>
    );
  }

  return (
    // Cap at 15em on tall screens, but never exceed ~45% of the viewport so the
    // portaled menu can't overflow past a short screen's edge and become
    // partially unreachable (CORE-4257).
    <Box ref={scrollContainerRef} maxH="min(15em, 45vh)" overflowY="auto">
      {inputValue?.trim()
        ? filteredItems.map((item) => {
            const isSelected = isMulti
              ? currentSelectedValue?.some((val) => val?.id === item?.id)
              : currentSelectedValue?.id === item?.id;

            return (
              <SearchResultItem
                key={item.id}
                item={item}
                items={data}
                onSelect={onSelect}
                isSelected={isSelected}
                isHighlighted={focusedItemId === item.id}
                itemRef={focusedItemId === item.id ? scrollToRef : null}
                onKeyDown={(e) => handleKeyDown(e, item)}
              />
            );
          })
        : rootItems?.map((item) => (
            <TreeItem
              key={item?.id}
              item={item}
              items={data}
              onClose={onClose}
              inputValue={inputValue}
              currentSelectedValue={currentSelectedValue}
              primaryParentStyles={primaryParentStyles}
              childrenStyles={childrenStyles}
              onSelect={onSelect}
              isMulti={isMulti}
              isHighlighted={false}
              itemRef={focusedItemId === item.id ? scrollToRef : null}
              onKeyDown={(e) => handleKeyDown(e, item)}
              focusedItemId={focusedItemId}
            />
          ))}
    </Box>
  );
};

export const SelectParentChildRootOption = (props) => {
  const { options = [], selectProps = {}, isMulti } = props;
  const {
    onChange,
    onMenuClose,
    inputValue = '',
    setInputText,
    value,
    menuIsOpen,
  } = selectProps;

  const handleSelect = (selectedOption, _isFromSearch) => {
    if (isMulti) {
      const currentValue = Array.isArray(value) ? [...value] : [];
      const optionExists = currentValue.some(
        (val) => val.id === selectedOption.id
      );

      let newValue;
      if (optionExists) {
        const childrenToRemove = getAllChildren(selectedOption, options);
        const idsToRemove = [
          selectedOption.id,
          ...childrenToRemove.map((c) => c.id),
        ];
        newValue = currentValue.filter((val) => !idsToRemove.includes(val.id));
      } else {
        // Always select the item and its children, regardless of whether it's ungrouped or from search
        const childrenToAdd = getAllChildren(selectedOption, options);
        const newItems = [selectedOption, ...childrenToAdd].filter(
          (item) => !currentValue.some((val) => val.id === item.id)
        );
        newValue = [...currentValue, ...newItems];
      }

      if (!Array.isArray(newValue)) {
        newValue = [newValue].filter(Boolean);
      }

      onChange(newValue);
    } else {
      onChange(selectedOption);
    }

    if (setInputText) {
      setInputText('');
    } else if (selectProps.onInputChange) {
      selectProps.onInputChange('', { action: 'set-value' });
    }

    onMenuClose();
  };

  return (
    <components.Menu {...props}>
      <NestedTreeList
        data={options}
        onSelect={handleSelect}
        onClose={onMenuClose}
        inputValue={inputValue}
        currentSelectedValue={value}
        isMulti={isMulti}
        menuIsOpen={menuIsOpen}
      />
    </components.Menu>
  );
};
